import { inject, Service } from '@angular/core';
import { CookieOptions, CookieService } from 'ngx-cookie';
import { TinyColor } from '@thebespokepixel/es-tinycolor';
import { Color } from './mng-theme.types';
import { addYears } from 'date-fns';
import { DEFAULT_ACCENT_COLOR, DEFAULT_PRIMARY_COLOR } from '@mng/mng-ts-helpers';

@Service()
export class MngThemeService {
  private cookieService = inject(CookieService);

  primaryColor: string;
  accentColor: string;

  primaryColorPalette: Color[] = [];
  accentColorPalette: Color[] = [];

  _darkMode = false;

  COOKIE_OPTIONS: CookieOptions = {
    expires: addYears(new Date(), 1),
    storeUnencoded: true,
    secure: true,
    sameSite: 'strict'
  };

  get darkMode(): boolean {
    return this._darkMode;
  }

  set darkMode(darkMode: boolean) {
    this._darkMode = darkMode;
    this.cookieService.put('darkMode', String(darkMode), this.COOKIE_OPTIONS);
    this.setPrimaryColor();
    this.setAccentColor();
    this.setOverlayClass();
  }

  initializeTheme() {
    const darkModeCookie = this.cookieService.get('darkMode');
    if (darkModeCookie) {
      this.darkMode = darkModeCookie === 'true';
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      this.darkMode = true;
    }
    this.setOverlayClass();

    this.setPrimaryColor(DEFAULT_PRIMARY_COLOR);

    this.setAccentColor(DEFAULT_ACCENT_COLOR);

    if (window.matchMedia('prefers-color-scheme: dark').matches) {
      this.darkMode = true;
    }
  }

  private setOverlayClass() {
    if (this.darkMode) {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    } else {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    }
  }

  private setPrimaryColor(color?: string) {
    if (color) {
      this.primaryColor = color;
    }
    this.primaryColorPalette = this.computeColors(this.primaryColor);

    for (const clr of this.primaryColorPalette) {
      const key1 = `--theme-primary-${clr.name}`;
      const value1 = clr.hex;
      const key2 = `--theme-primary-contrast-${clr.name}`;
      const value2 = clr.darkContrast ? 'rgba(0,0,0, 0.87)' : 'white';
      document.documentElement.style.setProperty(key1, value1);
      document.documentElement.style.setProperty(key2, value2);
    }
  }

  private setAccentColor(color?: string) {
    if (color) {
      this.accentColor = color;
    }
    this.accentColorPalette = this.computeColors(this.accentColor);

    for (const clr of this.accentColorPalette) {
      const key1 = `--theme-accent-${clr.name}`;
      const value1 = clr.hex;
      const key2 = `--theme-accent-contrast-${clr.name}`;
      const value2 = clr.darkContrast ? 'rgba(0,0,0, 0.87)' : 'white';
      document.documentElement.style.setProperty(key1, value1);
      document.documentElement.style.setProperty(key2, value2);

      if (clr.name === '500') {
        const key3 = `--theme-accent-light`;
        const value3 = new TinyColor(clr.hex).setAlpha(0.54).toRgbString();
        document.documentElement.style.setProperty(key3, value3);
      }
    }
  }

  private computeColors(hex: string): Color[] {
    return [
      this.getColorObject(new TinyColor(hex).lighten(52), '50'),
      this.getColorObject(new TinyColor(hex).lighten(37), '100'),
      this.getColorObject(new TinyColor(hex).lighten(26), '200'),
      this.getColorObject(new TinyColor(hex).lighten(12), '300'),
      this.getColorObject(new TinyColor(hex).lighten(6), '400'),
      this.getColorObject(new TinyColor(hex), '500'),
      this.getColorObject(new TinyColor(hex).darken(6), '600'),
      this.getColorObject(new TinyColor(hex).darken(12), '700'),
      this.getColorObject(new TinyColor(hex).darken(18), '800'),
      this.getColorObject(new TinyColor(hex).darken(24), '900'),
      this.getColorObject(new TinyColor(hex).lighten(50).saturate(30), 'A100'),
      this.getColorObject(new TinyColor(hex).lighten(30).saturate(30), 'A200'),
      this.getColorObject(new TinyColor(hex).lighten(10).saturate(15), 'A400'),
      this.getColorObject(new TinyColor(hex).lighten(5).saturate(5), 'A700')
    ];
  }

  private getColorObject(value, name): Color {
    const color = new TinyColor(value);
    const lightnessLimit = this.darkMode ? 100 : 100;
    return {
      name: name,
      hex: color.toHexString(false),
      darkContrast: color.getBrightness() > lightnessLimit
      // darkContrast: c.isLight()
    };
  }
}
